// BookingForm.jsx
import React, { useState, useEffect } from "react";
import { gql, useQuery, useMutation, useLazyQuery } from "@apollo/client";
import "../../index.css";
import VerifyBookingForm from "./VerifyBookingForm";
import OrderInventoryPage from "./OrderInventoryPage";
import { get, type } from "jquery";
import AdminEmployeeFeedbackForm from "./AdminEmployeeFeedbackForm";
import EditBooking from "./EditBooking";
import { DECLINE_CATEGORIES, DECLINE_REASONS } from "./declineOptions";

/* ===============================
   GraphQL Queries & Mutations
   (ensure your backend returns `order` on these mutations)
================================= */

const CANCEL_ADMIN_BOOKING = gql`
  mutation cancelAdminBooking($data: CancelAdminBookingInput!) {
    cancelAdminBooking(data: $data) {
      status
      message
      order {
        id
        price
        totalPrice
        status
        address
        bookingDate
        timeSlot
        paymentStatus
        pincode
        paymentType
        verificationStatus
        femaleTimeSlot
        bookingId
        bookingType
        adminPriceAdjustment
        adminPriceAdjustmentReason
        couponDiscount
        codCharges
        couponCode
        orderDetail {
          id
          name
          serviceId
          price
          specialPrice
          firstTimePrice
          cutPrice
          count
          type
        }
        employee {
          id
          fullName
        }
        user {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;

const SEND_CHECKIN_NUDGE = gql`
  mutation adminSendCheckinNudge($orderId: ID!) {
    adminSendCheckinNudge(orderId: $orderId) {
      status
      message
      lastSentAt
    }
  }
`;

const NOTIFY_RUNNING_LATE = gql`
  mutation adminNotifyCustomerRunningLate($data: AdminNotifyCustomerRunningLateInput!) {
    adminNotifyCustomerRunningLate(data: $data) {
      status
      message
    }
  }
`;

const MARK_AWAITING_RESPONSE = gql`
  mutation adminMarkAwaitingResponse($orderId: ID!) {
    adminMarkAwaitingResponse(orderId: $orderId) {
      status
      message
    }
  }
`;

const MARK_CUSTOMER_RESPONDED = gql`
  mutation adminMarkCustomerResponded($orderId: ID!, $note: String) {
    adminMarkCustomerResponded(orderId: $orderId, note: $note) {
      status
      message
    }
  }
`;

const GET_AVAILABLE_EMPLOYEES = gql`
  query GetAvailableEmployees($data: getAvailableEmployeeForSlot!) {
    getAvailableEmployeesForSlot(data: $data) {
      status
      message
      employees {
        id
        fullName
      }
    }
  }
`;

const REASSIGN_BOOKING = gql`
  mutation ReassignBooking($data: ReassignBookingInput!) {
    reassignBooking(data: $data) {
      status
      message
      order {
        id
        price
        totalPrice
        status
        address
        bookingDate
        timeSlot
        paymentStatus
        pincode
        paymentType
        verificationStatus
        femaleTimeSlot
        bookingId
        bookingType
        adminPriceAdjustment
        adminPriceAdjustmentReason
        couponDiscount
        codCharges
        couponCode
        orderDetail {
          id
          name
          serviceId
          price
          specialPrice
          firstTimePrice
          cutPrice
          count
          type
        }
        employee {
          id
          fullName
        }
        user {
          id
          fullName
          contactNumber
        }
      }
    }
  }
`;

/* ===============================
   Component
   props:
     - setEditBookingForm(editing boolean)
     - editingData (initial order object)
     - onBookingUpdated(updatedOrder)  <-- optional callback for parent to update lists
================================= */

const BookingForm = ({ setEditBookingForm, editingData, onBookingUpdated }) => {
  // local "order" state — initialize from prop and use everywhere to reflect updates
  const [order, setOrder] = useState(editingData);
  // GraphQL hooks
  const [
    getAvailableEmployees,
    { data: availableData, loading: availableLoading },
  ] = useLazyQuery(GET_AVAILABLE_EMPLOYEES, { fetchPolicy: "network-only" });
  const [reassignBooking] = useMutation(REASSIGN_BOOKING);

  // States (controlled from local order state where appropriate)
  const [mode, setMode] = useState("reassign"); // "edit" | "reassign" | etc
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [date, setDate] = useState(editingData.bookingDate);
  const [time, setTime] = useState(editingData.timeSlot);
  const [availableList, setAvailableList] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [reassignSkipSlotValidation, setReassignSkipSlotValidation] =
    useState(false);

  const [cancelReason, setCancelReason] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [declineCategory, setDeclineCategory] = useState("");
  const [declineReasonCode, setDeclineReasonCode] = useState("");
  const [lastNudgeSentAt, setLastNudgeSentAt] = useState(null);
  const [showRunningLateModal, setShowRunningLateModal] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState("");
  const [lastRunningLateSentAt, setLastRunningLateSentAt] = useState(null);
  const [showCustomerRespondedModal, setShowCustomerRespondedModal] =
    useState(false);
  const [customerResponseNote, setCustomerResponseNote] = useState("");

  const handleDeclineCategoryChange = (value) => {
    setDeclineCategory(value);
    setDeclineReasonCode("");
  };

  const [sendCheckinNudge, { loading: nudgeLoading }] =
    useMutation(SEND_CHECKIN_NUDGE);

  const handleSendCheckinNudge = async () => {
    if (!order?.id) return;
    try {
      const res = await sendCheckinNudge({ variables: { orderId: order.id } });
      const out = res?.data?.adminSendCheckinNudge;
      if (out?.status === "SUCCESS") {
        setLastNudgeSentAt(out.lastSentAt);
        alert(out.message || "Check-in message sent.");
      } else {
        alert(out?.message || "Failed to send check-in message.");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to send check-in message. Please try again.");
    }
  };
  const [notifyRunningLate, { loading: runningLateLoading }] =
    useMutation(NOTIFY_RUNNING_LATE);

  const handleNotifyRunningLate = async () => {
    if (!order?.id) return;
    const minutes = parseInt(delayMinutes, 10);
    if (!minutes || minutes <= 0) {
      alert("Please enter a valid number of minutes.");
      return;
    }
    try {
      const res = await notifyRunningLate({
        variables: { data: { orderId: order.id, delayMinutes: minutes } },
      });
      const out = res?.data?.adminNotifyCustomerRunningLate;
      if (out?.status === "SUCCESS") {
        setLastRunningLateSentAt(new Date().toISOString());
        setShowRunningLateModal(false);
        setDelayMinutes("");
        alert(out.message || "Customer notified.");
      } else {
        alert(out?.message || "Failed to notify customer.");
      }
    } catch (e) {
      console.error(e);
      alert("Failed to notify customer. Please try again.");
    }
  };

  const [markAwaitingResponse, { loading: markAwaitingResponseLoading }] =
    useMutation(MARK_AWAITING_RESPONSE);

  const handleMarkAwaitingResponse = async () => {
    if (!order?.id) return;
    try {
      const res = await markAwaitingResponse({
        variables: { orderId: order.id },
      });
      const out = res?.data?.adminMarkAwaitingResponse;
      alert(out?.message || "Booking marked as awaiting response.");
    } catch (e) {
      console.error(e);
      alert("Failed to mark this booking as awaiting response.");
    }
  };

  const [markCustomerResponded, { loading: markCustomerRespondedLoading }] =
    useMutation(MARK_CUSTOMER_RESPONDED);

  const handleMarkCustomerResponded = async () => {
    if (!order?.id) return;
    try {
      const res = await markCustomerResponded({
        variables: { orderId: order.id, note: customerResponseNote || null },
      });
      const out = res?.data?.adminMarkCustomerResponded;
      if (out?.status === "SUCCESS") {
        setShowCustomerRespondedModal(false);
        setCustomerResponseNote("");
      }
      alert(out?.message || "Customer response logged.");
    } catch (e) {
      console.error(e);
      alert("Failed to log the customer's response.");
    }
  };

  const [cancelBooking, { loading: cancelLoading }] =
    useMutation(CANCEL_ADMIN_BOOKING);

  // keep `order` in sync if parent sends different editingData later
  useEffect(() => {
    setOrder(editingData);
    setDate(editingData.bookingDate);
    setTime(editingData.timeSlot);
  }, [editingData]);

  // Load available employees for reassign
  useEffect(() => {
    if (availableData?.getAvailableEmployeesForSlot?.employees) {
      setAvailableList(availableData.getAvailableEmployeesForSlot.employees);
    }
  }, [availableData]);

  /* ============================
     Handlers (mutations) — now update local `order` instead of reloading
     ============================ */
  const handleToggleSkip = () => {
    setReassignSkipSlotValidation((prev) => !prev);
    setSelectedEmployee("");
    setAvailableList([]);
  };
  const handleCancelClick = () => {
    if (!order?.id) {
      alert("Order ID missing.");
      return;
    }
    setShowCancelModal(true);
  };
  const handleCancelConfirm = async () => {
    if (!cancelReason.trim() || !declineCategory || !declineReasonCode) {
      return; // button disabled in UI anyway
    }
    try {
      const res = await cancelBooking({
        variables: {
          data: {
            orderId: order.id,
            reason: cancelReason?.trim() || "Cancelled by admin",
            declineCategory,
            declineReason: declineReasonCode,
          },
        },
      });
      const out = res?.data?.cancelAdminBooking;
      if (out?.status === "SUCCESS") {
        // Use returned order if provided
        const updatedOrder = out.order || null;
        if (updatedOrder) {
          setOrder(updatedOrder);
          // inform parent
          if (typeof onBookingUpdated === "function")
            onBookingUpdated(updatedOrder);
        } else {
          // fallback: local optimistic update
          setOrder((prev) => ({
            ...prev,
            status: "Cancelled",
            verificationStatus: "CANCELLED",
          }));
          if (typeof onBookingUpdated === "function")
            onBookingUpdated({
              ...order,
              status: "Cancelled",
              verificationStatus: "CANCELLED",
            });
        }

        setShowCancelModal(false);
        setErrorMsg("");
        alert(out.message || "Booking cancelled successfully.");
      } else {
        alert(out?.message || "Failed to cancel booking.");
      }
    } catch (e) {
      console.error(e);
      alert("Cancel failed. Please try again.");
    }
  };

  const getEditModeStatus = (order) => {
    const nonEditableStatuses = ["cancelled", "declined"];
    const nonEditableVerificationStatuses = [
      "verified",
      "payment_verification_pending",
    ];

    const isCancelled = nonEditableStatuses.includes(
      order.status.toLowerCase(),
    );
    if (isCancelled) {
      return "cancelled";
    }
    const isVerfied = nonEditableVerificationStatuses.includes(
      order.verificationStatus.toLowerCase(),
    );
    if (isVerfied) {
      return "verified";
    }
    return "none";
  };

  const status = getEditModeStatus(order);

  useEffect(() => {
    if (!order) return;

    if (status === "cancelled") {
      setMode("feedback");
    }
    if (status === "verified") {
      setMode("verify");
      return;
    }
  }, [order, status]);
  const handleCheckAvailableEmployees = async (e) => {
    e?.preventDefault?.(); // may be invoked from button or form

    setErrorMsg("");
    // request available employees and show them
    try {
      await getAvailableEmployees({
        variables: {
          data: {
            femaleTimeSlot: order.femaleTimeSlot,
            date,
            address: order.address,
            timeSlot: time,
            serviceTime: order.totalTime || 60,
            skipSlotValidation: reassignSkipSlotValidation,
          },
        },
      });
    } catch (err) {
      console.error("Failed to fetch available employees:", err);
      setErrorMsg("Could not fetch available employees");
    }
  };

  const handleReassign = async () => {
    setErrorMsg("");
    if (!selectedEmployee) {
      setErrorMsg("Please pick an employee to reassign.");
      return;
    }
    try {
      const res = await reassignBooking({
        variables: {
          data: {
            orderId: order.id,
            employeeId: selectedEmployee,
            skipSlotValidation: reassignSkipSlotValidation,
          },
        },
      });

      if (res.data.reassignBooking.status === "SUCCESS") {
        const updatedOrder = res.data.reassignBooking.order || null;
        if (updatedOrder) {
          setOrder(updatedOrder);
          if (typeof onBookingUpdated === "function")
            onBookingUpdated(updatedOrder);
        } else {
          const pickedEmp = availableList.find(
            (e) => e.id === selectedEmployee,
          );
          setOrder((prev) => ({
            ...prev,
            employee: pickedEmp || {
              id: selectedEmployee,
              fullName: "Assigned",
            },
          }));
          if (typeof onBookingUpdated === "function")
            onBookingUpdated({
              ...order,
              employee: availableList.find(
                (e) => e.id === selectedEmployee,
              ) || { id: selectedEmployee, fullName: "Assigned" },
            });
        }

        setErrorMsg("");
        alert(res.data.reassignBooking.message || "Reassigned successfully");
      } else {
        setErrorMsg(res.data.reassignBooking.message || "Reassign failed");
      }
    } catch (error) {
      console.error(error);
      setErrorMsg("Failed to reassign booking");
    }
  };

  /* ============================
     Render
     ============================ */

  // previously selected services names (from local order if present)
  const previouslySelected =
    order?.orderDetail?.map((s) => s.name) ||
    editingData.orderDetail.map((s) => s.name);

  return (
    <div className="flex flex-col space-y-6">
      {/* Back Button */}
      <button
        className="self-start px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
        onClick={() => {
          setEditBookingForm(false);
          console.log("Closing Booking Form", typeof onBookingUpdated);
          onBookingUpdated(order);
        }}
      >
        ← Back to Bookings
      </button>

      <h3 className="text-[#FBB033] font-bold text-2xl">Manage Booking</h3>

      {/* Mode Switch */}
      <div className="flex flex-wrap gap-2 sm:gap-4">
        {status !== "cancelled" && status !== "verified" && (
          <button
            className={`px-4 py-2 rounded ${
              mode === "reassign"
                ? "bg-[#FBB034] text-black"
                : "bg-gray-700 text-white"
            }`}
            onClick={() => setMode("reassign")}
          >
            Reassign Booking
          </button>
        )}
        {status !== "cancelled" && status !== "verified" && (
          <button
            className={`px-4 py-2 rounded ${
              mode === "edit"
                ? "bg-[#FBB034] text-black"
                : "bg-gray-700 text-white"
            }`}
            onClick={() => setMode("edit")}
          >
            Edit Booking
          </button>
        )}
        {status !== "cancelled" && status !== "verified" && (
          <button
            className={`px-4 py-2 rounded ${
              mode === "cancel"
                ? "bg-[#FBB034] text-black"
                : "bg-gray-700 text-white"
            }`}
            onClick={() => setMode("cancel")}
          >
            Cancel Booking
          </button>
        )}
        {status !== "cancelled" && (
          <button
            className={`px-4 py-2 rounded ${
              mode === "verify"
                ? "bg-[#FBB034] text-black"
                : "bg-gray-700 text-white"
            }`}
            onClick={() => setMode("verify")}
          >
            Verify Booking
          </button>
        )}
        {status !== "cancelled" && (
          <button
            className={`px-4 py-2 rounded ${
              mode === "verifyInventory"
                ? "bg-[#FBB034] text-black"
                : "bg-gray-700 text-white"
            }`}
            onClick={() => setMode("verifyInventory")}
          >
            Verify Inventory
          </button>
        )}
        <button
          className={`px-4 py-2 rounded ${
            mode === "feedback"
              ? "bg-[#FBB034] text-black"
              : "bg-gray-700 text-white"
          }`}
          onClick={() => setMode("feedback")}
        >
          Feedback
        </button>
        {!["Completed", "Cancelled", "Declined"].includes(order.status) && (
          <button
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
            onClick={handleSendCheckinNudge}
            disabled={nudgeLoading}
            title="Send a WhatsApp check-in message if the customer isn't answering calls"
          >
            {nudgeLoading ? "Sending..." : "Send Check-in WhatsApp"}
          </button>
        )}
        {!["Completed", "Cancelled", "Declined"].includes(order.status) && (
          <button
            className="px-4 py-2 rounded bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-50"
            onClick={() => setShowRunningLateModal(true)}
            disabled={runningLateLoading}
            title="Notify the customer that our professional is running late"
          >
            {runningLateLoading ? "Sending..." : "Notify Running Late"}
          </button>
        )}
        {!["Completed", "Cancelled", "Declined", "OnHold"].includes(
          order.status,
        ) &&
          (order.noShowHoldStatus === "AWAITING_RESPONSE" ? (
            <button
              className="px-4 py-2 rounded bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
              onClick={() => setShowCustomerRespondedModal(true)}
              disabled={markCustomerRespondedLoading}
              title="Log that the customer responded, cancelling the no-show clock"
            >
              Customer Responded
            </button>
          ) : (
            <button
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
              onClick={handleMarkAwaitingResponse}
              disabled={markAwaitingResponseLoading}
              title="Start the no-show clock - frees the professional if the customer doesn't respond in time"
            >
              {markAwaitingResponseLoading
                ? "Marking..."
                : "Mark Awaiting Response"}
            </button>
          ))}
      </div>
      {lastNudgeSentAt && (
        <p className="text-xs text-gray-400">
          Last check-in message sent at {new Date(lastNudgeSentAt).toLocaleTimeString()}
        </p>
      )}
      {lastRunningLateSentAt && (
        <p className="text-xs text-gray-400">
          Last running-late notice sent at {new Date(lastRunningLateSentAt).toLocaleTimeString()}
        </p>
      )}
      {showRunningLateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setShowRunningLateModal(false)}
          />
          <div className="relative z-10 w-full max-w-md mx-4 bg-[#1a1a1a] text-white rounded-xl p-6 shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-[#FBB034]">
              Notify Customer: Running Late
            </h3>
            <p className="text-sm text-gray-300 mb-4">
              How many minutes late is our professional running? The customer will get a WhatsApp message with this delay.
            </p>
            <div className="mb-4">
              <label className="text-sm block mb-1">Delay (minutes)</label>
              <input
                type="number"
                min="1"
                value={delayMinutes}
                onChange={(e) => setDelayMinutes(e.target.value)}
                placeholder="e.g. 15"
                className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md border border-gray-600 text-gray-200"
                onClick={() => setShowRunningLateModal(false)}
                disabled={runningLateLoading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-50"
                onClick={handleNotifyRunningLate}
                disabled={runningLateLoading}
              >
                {runningLateLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCustomerRespondedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => setShowCustomerRespondedModal(false)}
          />
          <div className="relative z-10 w-full max-w-md mx-4 bg-[#1a1a1a] text-white rounded-xl p-6 shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-[#FBB034]">
              Customer Responded
            </h3>
            <p className="text-sm text-gray-300 mb-4">
              Logs the response and cancels the no-show clock - the booking
              stays as-is, still assigned to the professional.
            </p>
            <div className="mb-4">
              <label className="text-sm block mb-1">Note (optional)</label>
              <textarea
                value={customerResponseNote}
                onChange={(e) => setCustomerResponseNote(e.target.value)}
                placeholder="e.g. customer said running 15 min late"
                rows={3}
                className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md border border-gray-600 text-gray-200"
                onClick={() => setShowCustomerRespondedModal(false)}
                disabled={markCustomerRespondedLoading}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
                onClick={handleMarkCustomerResponded}
                disabled={markCustomerRespondedLoading}
              >
                {markCustomerRespondedLoading ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Previously selected services row */}
      <div className="flex flex-wrap gap-2 mt-2">
        {previouslySelected.map((name, idx) => (
          <div
            key={idx}
            className="px-2 py-1 bg-gray-600 text-white rounded text-sm"
          >
            {name}
          </div>
        ))}
      </div>

      {/* Edit Mode */}
      {mode === "edit" && (
        <EditBooking
          editingData={editingData}
          order={order}
          onOrderUpdated={(updatedOrder) => {
            if (!updatedOrder) return;
            setOrder(updatedOrder);
            if (typeof onBookingUpdated === "function") {
              onBookingUpdated(updatedOrder);
            }
          }}
          onError={setErrorMsg}
        />
      )}

      {/* Cancel Mode */}
      {mode === "cancel" && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-start mt-6 gap-2 sm:gap-0">
            <span className="text-white text-sm w-full sm:w-1/6 sm:pt-1">Reason:</span>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Write the reason for cancellation/changes"
              className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full sm:w-4/6 h-24"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start mt-2 gap-2 sm:gap-0">
            <span className="text-white text-sm w-full sm:w-1/6 sm:pt-1">Fault:</span>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-4/6">
              <select
                value={declineCategory}
                onChange={(e) => handleDeclineCategoryChange(e.target.value)}
                className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full sm:w-1/2"
              >
                {DECLINE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <select
                value={declineReasonCode}
                onChange={(e) => setDeclineReasonCode(e.target.value)}
                disabled={!declineCategory}
                className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full sm:w-1/2 disabled:opacity-50"
              >
                {(DECLINE_REASONS[declineCategory] || [{ value: "", label: "Select reason" }]).map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-start sm:justify-between items-center py-8 w-full sm:w-4/6 sm:ml-[16.666667%]">
            <button
              className="bg-red-500 hover:bg-red-600 text-white text-sm font-bold px-5 py-2 rounded-md"
              onClick={handleCancelClick}
              disabled={!order?.id}
            >
              Cancel Booking
            </button>
          </div>
          {showCancelModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center"
              role="dialog"
              aria-modal="true"
            >
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/70"
                onClick={() => setShowCancelModal(false)}
              />

              {/* Modal */}
              <div className="relative z-10 w-full max-w-md mx-4 bg-[#1a1a1a] text-white rounded-xl p-6 shadow-xl">
                <h3 className="text-lg font-bold mb-2 text-[#FBB034]">
                  Confirm Cancellation
                </h3>
                <p className="text-sm text-gray-300 mb-4">
                  Are you sure you want to cancel this booking?
                </p>

                <div className="mb-4">
                  <label className="text-sm block mb-1">Reason</label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Enter cancellation reason"
                    className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-full h-24"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Reason is required to proceed.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="text-sm block mb-1">Fault</label>
                  <div className="flex gap-2">
                    <select
                      value={declineCategory}
                      onChange={(e) => handleDeclineCategoryChange(e.target.value)}
                      className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-1/2"
                    >
                      {DECLINE_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    <select
                      value={declineReasonCode}
                      onChange={(e) => setDeclineReasonCode(e.target.value)}
                      disabled={!declineCategory}
                      className="bg-[#0c0b0b] text-white border border-gray-700 rounded-md px-3 py-2 w-1/2 disabled:opacity-50"
                    >
                      {(DECLINE_REASONS[declineCategory] || [{ value: "", label: "Select reason" }]).map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Fault category and reason are required to proceed.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-md border border-gray-600 text-gray-200"
                    onClick={() => setShowCancelModal(false)}
                    disabled={cancelLoading}
                  >
                    Close
                  </button>
                  <button
                    className={`px-4 py-2 rounded-md font-bold ${
                      cancelReason.trim() && declineCategory && declineReasonCode
                        ? "bg-red-500 hover:bg-red-600 text-white"
                        : "bg-gray-600 text-gray-300 cursor-not-allowed"
                    }`}
                    onClick={handleCancelConfirm}
                    disabled={!cancelReason.trim() || !declineCategory || !declineReasonCode || cancelLoading}
                  >
                    {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Reassign Mode */}
      {mode === "reassign" && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-3">
              <label className="text-white text-sm">
                Book without slot validation:
              </label>
              <button
                onClick={handleToggleSkip}
                className={`px-3 py-1 rounded ${
                  reassignSkipSlotValidation
                    ? "bg-[#FBB034] text-black"
                    : "bg-[#0c0b0b] text-white border border-[#E0E0E0]"
                }`}
              >
                {reassignSkipSlotValidation ? "ON" : "OFF"}
              </button>
            </div>
            <p className="text-gray-400 text-sm">
              When ON, admin must pick an employee manually and booking will be
              created without slot validation/assignment.
            </p>
          </div>
          <div className="flex justify-center py-8">
            <button
              className="bg-blue-500 text-sm font-bold px-5 py-2 rounded-md text-white"
              onClick={handleCheckAvailableEmployees}
            >
              Check Available Employees
            </button>
          </div>
          {availableLoading && (
            <p className="text-gray-400 text-center">
              Loading available employees...
            </p>
          )}
          {!availableLoading &&
            availableData &&
            availableData.getAvailableEmployeesForSlot?.employees?.length ===
              0 && (
              <p className="text-red-400 text-center">
                No available employees for this slot.
              </p>
            )}
          {availableList.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-0">
              <span className="text-white text-sm w-full sm:w-1/6">Select Employee:</span>
              <select
                className="bg-[#0c0b0b] text-white border-b border-[#E0E0E0] px-2 py-1 w-full sm:w-4/6"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                <option value="" disabled hidden>
                  Select Employee
                </option>
                {availableList.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.fullName}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedEmployee && (
            <div className="flex justify-center py-8">
              <button
                className="bg-green-500 text-sm font-bold px-5 py-2 rounded-md text-white"
                onClick={handleReassign}
              >
                Reassign Booking
              </button>
            </div>
          )}
        </>
      )}

      {/* Verify & Inventory */}
      {mode === "verify" && (
        <VerifyBookingForm
          editingData={order}
          onUpdate={(updatedOrder) => {
            // update local order and inform parent
            if (updatedOrder) {
              setOrder(updatedOrder);
              if (typeof onBookingUpdated === "function")
                onBookingUpdated(updatedOrder);
            }
          }}
        />
      )}
      {mode === "verifyInventory" && (
        <OrderInventoryPage
          editingData={order}
          orderId={order.id}
          employeeId={order.employee?.id}
          onUpdate={(updatedOrder) => {
            // update local order and inform parent
            if (updatedOrder) {
              setOrder(updatedOrder);
              if (typeof onBookingUpdated === "function")
                onBookingUpdated(updatedOrder);
            }
          }}
        />
      )}
      {mode === "feedback" && (
        <AdminEmployeeFeedbackForm
          order={order}
          onClose={() => setMode("view")}
        />
      )}

      {/* Error Message */}
      {errorMsg && <p className="text-red-500 text-center">{errorMsg}</p>}
    </div>
  );
};

export default BookingForm;
